import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DxTreeListComponent } from 'devextreme-angular';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { forkJoin, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CountryOption } from 'src/app/Modules/cdms/models/competency-module.model';
import { CompetencyModuleApiService } from 'src/app/Modules/cdms/services/competency-module-api.service';
import { CustomToastService } from 'src/app/services/custom-toast.service';

// Union type identifying every data category shown in the DB Lookup tree.
type LookupSection = 'assessmentCategories' | 'contractStatuses' | 'grades' | 'jobTitles' | 'skills' | 'regions' | 'countries' | 'bases' | 'trainingVenues';

// Static configuration for each top-level section: maps a key to its root node id, display label, CRUD flag and optional location type.
interface LookupSectionConfig {
    key: LookupSection;
    rootId: string;
    label: string;
    supportsCrud: boolean;
    locationType?: number;
}

// Flat node used as a row in the DevExtreme tree-list. 'group' rows are section headers; 'item' rows are real data entries.
interface LookupTreeNode {
    id: string;
    parentId: string | number;
    name: string;
    section: LookupSection;
    rowType: 'group' | 'item';
    supportsCrud: boolean;
    entityId?: string;
    index?: number | null;
    comments?: string | null;
    countryId?: string | null;
    regionId?: string | null;
    code?: string | null;
    locationType?: number | null;
}

// Ordered list of sections that appear in the tree. Order determines display sequence.
const LOOKUP_SECTION_CONFIG: LookupSectionConfig[] = [
    { key: 'assessmentCategories', rootId: 'root-assessment-categories', label: 'Assessment Category', supportsCrud: true },
    { key: 'contractStatuses', rootId: 'root-contract-statuses', label: 'Contract Status', supportsCrud: true },
    { key: 'grades', rootId: 'root-grades', label: 'Grade', supportsCrud: true },
    { key: 'jobTitles', rootId: 'root-job-titles', label: 'Job Titles', supportsCrud: true },
    { key: 'skills', rootId: 'root-skills', label: 'Main Skill', supportsCrud: true },
    { key: 'regions', rootId: 'root-regions', label: 'Regions', supportsCrud: true },
    { key: 'trainingVenues', rootId: 'root-training-venues', label: 'Training / Assessment Venue', supportsCrud: true, locationType: 2 },
];

@Component({
    selector: 'app-db-lookup-grade-matrix',
    templateUrl: './db-lookup-grade-matrix.component.html',
    styleUrls: ['./db-lookup-grade-matrix.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class DbLookupGradeMatrixComponent implements OnInit, OnDestroy {
    @Input() form: UntypedFormGroup;
    @Input() isDbLookUpView = true;
    @Input() isGradeMatrixView = true;
    @ViewChild('treeList', { static: false }) treeList: DxTreeListComponent;
    @ViewChild('treeListGradeMatrix', { static: false }) treeListGradeMatrix: DxTreeListComponent;
    @ViewChild('unsavedChangesModal', { static: false }) unsavedChangesModal: TemplateRef<any>;

    destroy$ = new Subject<void>();
    // Save/Reset button state for DB Lookup and Grade Matrix panels.
    disableEdit = true;
    disableCancel = true;
    disableEditGrade = true;
    disableCancelGrade = true;
    // Second column is always visible; enabled only for contractStatuses, countries and trainingVenues.
    showSecondColumn = true;
    // Flat node array that drives the DB Lookup tree-list.
    data: LookupTreeNode[] = [];
    // Static hierarchy used by the Grade Matrix tree-list.
    gradeMatrixData: any[] = [];
    // Available country options for the Training Venue country selector.
    countryOptions: CountryOption[] = [];
    // Tracks which section has pending batch edits to intercept cross-section navigation.
    activeEditingSection: LookupSection | null = null;
    // Row key the user tried to navigate to while unsaved changes exist.
    pendingNavigationRowKey: string | null = null;
    // Row key to focus after data reload following a save.
    pendingFocusRowKey: string | null = null;
    // Set to true when the unsaved-changes modal chose "Save" so navigation resumes after the API call.
    proceedAfterSave = false;
    unsavedChangesModalRef: NgbModalRef | null = null;

    constructor(
        private ngxUiLoaderService: NgxUiLoaderService,
        private customToastService: CustomToastService,
        private cd: ChangeDetectorRef,
        private competencyApiService: CompetencyModuleApiService,
        private modalService: NgbModal
    ) {}

    ngOnInit(): void {
        // Seed the static grade matrix data, then load API data only when the DB Lookup panel is shown.
        this.initializeGradeMatrixData();
        if (this.isDbLookUpView) {
            this.loadData();
        }
    }

    // Fetches all lookup sections in parallel, merges country lists, builds the flat tree node array and resets edit state.
    loadData(): void {
        this.ngxUiLoaderService.start();
        forkJoin({
            assessmentCategories: this.competencyApiService.getAssessmentCategories(),
            contractStatuses: this.competencyApiService.getContractStatuses(),
            grades: this.competencyApiService.getGrades(),
            jobTitles: this.competencyApiService.getJobTitles(),
            skills: this.competencyApiService.getSkills(),
            regionHierarchy: this.competencyApiService.getRegionHierarchy(),
            trainingVenues: this.competencyApiService.getTrainingVenues(),
            countries: this.competencyApiService.getCountries(),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    // Prefer the dedicated countries endpoint; fall back to countries embedded in the region hierarchy.
                    const hierarchyCountries = this.flattenCountriesFromHierarchy(response?.regionHierarchy?.response || []);
                    const apiCountries = response?.countries?.response || [];
                    this.countryOptions = this.deduplicateCountries(apiCountries.length ? apiCountries : hierarchyCountries);
                    this.data = this.buildLookupTreeData(response);
                    this.disableEdit = true;
                    this.disableCancel = true;
                    this.activeEditingSection = null;
                    this.showSecondColumn = true;
                    this.ngxUiLoaderService.stop();
                    this.focusPendingRowIfNeeded();
                    this.cd.markForCheck();
                },
                error: () => {
                    this.customToastService.errorToast('Failed to load DB lookup data', 'Error');
                    this.ngxUiLoaderService.stop();
                    this.cd.markForCheck();
                },
            });
    }

    // Programmatically triggers the DevExtreme tree-list add-row action (used by the + toolbar button).
    addNewRow(): void {
        if (this.treeList) {
            this.treeList.instance.addRow();
        }
    }

    // Called by DevExtreme before a new row is added. Derives the child section, parent link and
    // any inherited properties (regionId, countryId, locationType) from the parent node.
    onInitNewRow(e: any): void {
        const parentId = e.parentKeys?.[0] ?? e.data.parentId;
        const parentNode = this.findNodeById(parentId);

        e.data.parentId = parentNode?.id ?? parentId ?? 0;
        e.data.rowType = 'item';
        e.data.supportsCrud = true;

        // Region item → child is a country; Country item → child is a base; otherwise inherit parent section.
        if (parentNode?.section === 'regions' && parentNode?.rowType === 'item') {
            e.data.section = 'countries';
            e.data.regionId = parentNode.entityId;
            e.data.locationType = null;
        } else if (parentNode?.section === 'countries' && parentNode?.rowType === 'item') {
            e.data.section = 'bases';
            e.data.countryId = parentNode.entityId;
            e.data.regionId = parentNode.regionId ?? null;
            e.data.locationType = 1;
        } else {
            e.data.section = parentNode?.section;
            e.data.supportsCrud = parentNode?.supportsCrud ?? false;
            e.data.locationType = parentNode?.locationType ?? null;
        }

        if (parentNode?.section === 'assessmentCategories' && (e.data.index == null || e.data.index === '')) {
            e.data.index = this.getNextAssessmentCategoryIndex();
        }

        this.updateSecondColumnVisibilityBySection(e.data?.section);

        this.updateDbLookupActionButtons();
        this.cd.markForCheck();
    }

    // Re-evaluate Save/Reset button state after any batch-edit change.
    onRowInserted(): void {
        this.updateDbLookupActionButtons();
    }

    onRowUpdated(): void {
        this.updateDbLookupActionButtons();
    }

    onRowRemoved(): void {
        this.updateDbLookupActionButtons();
    }

    // Grade Matrix new-row handler – ensures parent linkage and enables the Save/Reset buttons.
    onInitNewGradeRow(e: any): void {
        e.data.parentId ??= e.parentKeys?.[0] ?? 0;
        this.disableEditGrade = false;
        this.disableCancelGrade = false;
    }

    onGradeRowInserted(): void {
        this.disableEditGrade = false;
        this.disableCancelGrade = false;
    }

    onGradeRowUpdated(): void {
        this.disableEditGrade = false;
        this.disableCancelGrade = false;
    }

    onGradeRowRemoved(): void {
        this.disableEditGrade = false;
        this.disableCancelGrade = false;
    }

    // Remove DevExtreme's default toolbar so custom save/reset buttons outside the grid are used instead.
    customizeToolbar(e: any): void {
        e.toolbarOptions.items = [];
    }

    // Fires on every DevExtreme option change; only act when the batch-edit changes list is modified
    // so we can track which section is being edited and refresh button state.
    onDbLookupOptionChanged(e: any): void {
        if (e.fullName !== 'editing.changes') {
            return;
        }

        this.activeEditingSection = this.resolveActiveEditingSection();
        this.updateDbLookupActionButtons();
    }

    // On row click: intercept cross-section navigation if edits exist, update second-column
    // visibility, then put the row into edit mode if it is an editable item.
    onDbLookupRowClick(e: any): void {
        if (this.tryInterceptNavigation(e?.data, e?.key, e?.event)) {
            return;
        }

        this.updateSecondColumnVisibilityBySection(e?.data?.section);

        const rowData: LookupTreeNode | undefined = e?.data;
        if (e?.rowType === 'data' && rowData?.rowType === 'item' && rowData?.supportsCrud) {
            this.treeList?.instance?.editRow?.(e?.rowIndex);
        }
    }

    // On cell click: same navigation guard and second-column sync as row click (handles cases
    // where only a specific cell is targeted, e.g. the delete icon).
    onDbLookupCellClick(e: any): void {
        if (this.tryInterceptNavigation(e?.data, e?.key, e?.event)) {
            return;
        }

        this.updateSecondColumnVisibilityBySection(e?.data?.section);
    }

    // Configures each cell editor before it renders: sets disabled state, placeholder and custom
    // value-change handlers that push values into the batch-edit buffer and refresh button state.
    onDbLookupEditingStart(e: any): void {
        const rowData: LookupTreeNode = e?.data;
        const isEditableItem = rowData?.rowType === 'item' && rowData?.supportsCrud;
        if (!isEditableItem) {
            e.cancel = true;
        }
    }

    onGradeEditingStart(e: any): void {
        const rowData = e?.data;
        const hasChildren = this.hasChildren(rowData, this.gradeMatrixData);
        if (hasChildren) {
            e.cancel = true;
        }
    }

    onEditorPreparing(e: any): void {
        if (e.parentType !== 'dataRow') {
            return;
        }

        const rowData: LookupTreeNode = e.row?.data;
        // Only items (not section header groups) with CRUD support are editable.
        const isEditableItem = rowData?.rowType === 'item' && rowData?.supportsCrud;

        if (e.dataField === 'name') {
            e.editorOptions.disabled = !isEditableItem;
            e.editorOptions.placeholder = this.getNamePlaceholder(rowData?.section);

            if (isEditableItem) {
                const previousOnValueChanged = e.editorOptions.onValueChanged;
                e.editorOptions.valueChangeEvent = 'keyup input change';
                e.editorOptions.onValueChanged = (args: any) => {
                    if (typeof previousOnValueChanged === 'function') {
                        previousOnValueChanged(args);
                    }

                    e.setValue?.(args?.value);
                    this.updateDbLookupActionButtons();
                };
            }

            return;
        }

        if (e.dataField === 'index') {
            e.editorOptions.disabled = !(isEditableItem && rowData?.section === 'assessmentCategories');
            e.editorOptions.placeholder = 'Enter index';
            return;
        }

        if (e.dataField === 'comments') {
            e.editorOptions.disabled = !(isEditableItem && rowData?.section === 'contractStatuses');
            e.editorOptions.placeholder = 'Enter comment';
            return;
        }

        // The 'code' column is shared: country code for countries, comment for contract statuses,
        // and a country drop-down (dxSelectBox) for training venues. Disabled for all other sections.
        if (e.dataField === 'code') {
            const isCountryRow = isEditableItem && rowData?.section === 'countries';
            const isContractStatusRow = isEditableItem && rowData?.section === 'contractStatuses';
            const isTrainingVenueRow = isEditableItem && rowData?.section === 'trainingVenues';
            e.editorOptions.disabled = !(isCountryRow || isContractStatusRow || isTrainingVenueRow);
            e.editorOptions.placeholder = isCountryRow ? 'Enter country code' : isContractStatusRow ? 'Enter comment' : '';

            // Training venues use a searchable select box bound to countryId.
            if (isTrainingVenueRow) {
                e.editorName = 'dxSelectBox';
                e.editorOptions.dataSource = this.countryOptions;
                e.editorOptions.displayExpr = (country: CountryOption) => {
                    if (!country) {
                        return '';
                    }

                    const label = country?.code || country?.name || '';
                    if (!country?.name || country?.name === label) {
                        return label;
                    }

                    return `${label} - ${country.name}`;
                };
                e.editorOptions.valueExpr = 'countryId';
                e.editorOptions.searchEnabled = true;
                e.editorOptions.showClearButton = true;
                e.editorOptions.placeholder = 'Select country';
                e.editorOptions.value = rowData?.countryId ?? null;

                const previousOnValueChanged = e.editorOptions.onValueChanged;
                e.editorOptions.onValueChanged = (args: any) => {
                    if (typeof previousOnValueChanged === 'function') {
                        previousOnValueChanged(args);
                    }

                    e.setValue?.(args?.value);
                    this.updateDbLookupActionButtons();
                };

                return;
            }

            if (isCountryRow || isContractStatusRow) {
                const previousOnValueChanged = e.editorOptions.onValueChanged;
                e.editorOptions.valueChangeEvent = 'keyup input change';
                e.editorOptions.onValueChanged = (args: any) => {
                    if (typeof previousOnValueChanged === 'function') {
                        previousOnValueChanged(args);
                    }

                    e.setValue?.(args?.value);
                    this.updateDbLookupActionButtons();
                };
            }

            return;
        }

        if (e.dataField === 'countryId') {
            e.editorOptions.disabled = !(isEditableItem && rowData?.section === 'trainingVenues');
        }
    }

    // Applies a CSS class to rows that are queued for deletion so the user gets visual feedback
    // before the batch save is committed.
    onDbLookupRowPrepared(e: any): void {
        if (e.rowType !== 'data') {
            return;
        }

        const markedForDelete = this.isRowMarkedForDelete(e.key);
        if (markedForDelete) {
            e.rowElement.classList.add('pending-delete-row');
            return;
        }

        e.rowElement.classList.remove('pending-delete-row');
    }

    // DevExtreme validation rule: rejects null values and whitespace-only strings.
    validateNonWhitespaceValue = (e: any): boolean => {
        if (e?.value == null) {
            return false;
        }

        return String(e.value).trim().length > 0;
    };

    // DevExtreme validation rule: allows only A-Z, a-z, 0-9, space and . , ! ? ' " - _ ( ).
    validateAllowedCharacters = (e: any): boolean => {
        const rowData: LookupTreeNode | undefined = e?.data;

        // Training venue code editor is a country select-box; validate selection elsewhere.
        if (rowData?.section === 'trainingVenues' && e?.column?.dataField === 'code') {
            return true;
        }

        const value = String(e?.value ?? '');
        if (!value.length) {
            return true;
        }

        return this.containsOnlyAllowedCharacters(value);
    };

    // DevExtreme validation rule: ensures no duplicate name exists within the same section/parent layer.
    validateUniqueLookupValue = (e: any): boolean => {
        const normalizedInput = this.normalizeLookupValue(e?.value);
        if (!normalizedInput) {
            return true;
        }

        const layerScope = this.resolveLayerScope(e?.data);
        if (!layerScope) {
            return true;
        }

        return !this.hasDuplicateInLayer(layerScope.section, layerScope.parentId, e?.data, normalizedInput);
    };

    // DevExtreme validation rule: country code must be 1–5 characters (only applies to country rows).
    validateCountryCode = (e: any): boolean => {
        const rowData: LookupTreeNode | undefined = e?.data;
        console.log('rowData');
        console.log(rowData);
        if (rowData?.section !== 'countries') {
            console.log("rowData?.section !== 'countries'");
            return true;
        }

        const code = String(rowData?.code ?? '').trim();
        console.log('Validating country code:', String(code.length > 0 && code.length <= 5));
        return code.length > 0 && code.length <= 5;
    };

    // DevExtreme validation rule: a training venue must have a country selected.
    // Checks e.value (editor value), then falls back to the row's existing countryId/code so
    // that name-only edits do not trigger a spurious validation failure.
    validateTrainingVenueCountrySelection = (e: any): boolean => {
        const rowData: LookupTreeNode | undefined = e?.data;
        if (rowData?.section !== 'trainingVenues') {
            return true;
        }

        // Always check all sources: e.value (editor value when user interacted),
        // then rowData.countryId / rowData.code (existing values when only name was edited).
        // The TS-side hasAnyLookupValidationError already disables the save button when
        // the user explicitly clears the country, so this validator just needs to pass
        // when a valid country exists from any source.
        const selectedCountryId =
            this.resolveCountryIdFromSelection(e?.value) ||
            this.resolveCountryIdFromSelection(rowData?.countryId) ||
            this.resolveCountryIdFromSelection(rowData?.code);
        return Boolean(selectedCountryId);
    };

    // DevExtreme validation rule: grade name must be an integer between 1 and 5 (only for grade rows).
    validateGradeValue = (e: any): boolean => {
        const rowData: LookupTreeNode | undefined = e?.data;
        if (rowData?.section !== 'grades') {
            return true;
        }

        return this.isValidGradeValue(e?.value);
    };

    // calculateCellValue callback for the shared second column:
    // returns comments for contract statuses, country name for training venues, country code for countries.
    getSharedCodeOrCommentValue = (rowData: LookupTreeNode): string => {
        if (rowData?.section === 'contractStatuses') {
            return rowData?.comments ?? '';
        }

        if (rowData?.section === 'trainingVenues') {
            const matchedCountry = this.countryOptions.find((country) => country.countryId === rowData?.countryId);
            return matchedCountry?.name ?? '';
        }

        return rowData?.code ?? '';
    };

    // setCellValue callback for the shared second column: routes the edited value to the correct
    // field on newData depending on the row's section (comments / countryId+code / code).
    setSharedCodeOrCommentValue = (newData: Partial<LookupTreeNode>, value: any, currentRowData: LookupTreeNode): void => {
        if (currentRowData?.section === 'contractStatuses') {
            newData.comments = String(value ?? '');
            return;
        }

        if (currentRowData?.section === 'trainingVenues') {
            const selectedCountryId = String(value ?? '').trim();
            const matchedCountry = this.countryOptions.find((country) => country.countryId === selectedCountryId);
            newData.countryId = selectedCountryId || null;
            newData.code = matchedCountry?.code ?? null;
            return;
        }

        newData.code = String(value ?? '');
    };

    // Modal action: discard pending changes and navigate to the row the user was trying to reach.
    onUnsavedChangesUndo(modal: any): void {
        this.proceedAfterSave = false;
        this.undoAllChanges();
        modal.close('undo');
        this.proceedToPendingRow();
    }

    // Modal action: save pending changes first; navigation will resume after the API call succeeds.
    onUnsavedChangesSave(modal: any): void {
        this.proceedAfterSave = true;
        modal.close('save');
        this.saveAllChanges();
    }

    // Intercepts DevExtreme's default batch-save (e.cancel = true) and replaces it with
    // custom API calls. Flow: validate changes → build per-section API payloads → fire in parallel
    // via forkJoin → reload data on success or show error toast on failure.
    handleSaving(e: any): void {
        e.cancel = true;

        const changes = e.changes || [];
        if (!changes.length) {
            return;
        }

        const result = this.buildPersistenceRequests(changes);
        if (result.error) {
            // A validation/payload-building error was found; show it and abort.
            this.customToastService.errorToast(result.error, 'Error');
            return;
        }

        if (!result.requests.length) {
            // All changes were no-ops (e.g. deleted unsaved insert); just cancel edit mode.
            this.treeList?.instance.cancelEditData();
            this.disableEdit = true;
            this.disableCancel = true;
            this.cd.markForCheck();
            return;
        }

        this.ngxUiLoaderService.start();
        forkJoin(result.requests)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (responses: any[]) => {
                    const failedResponse = (responses || []).find((res: any) => res?.success === false);
                    if (failedResponse) {
                        const errorMessage = failedResponse?.message || 'Failed to save DB lookup changes';
                        this.ngxUiLoaderService.stop();
                        this.customToastService.errorToast(errorMessage, 'Error');
                        this.cd.markForCheck();
                        return;
                    }

                    this.treeList?.instance.cancelEditData();
                    this.disableEdit = true;
                    this.disableCancel = true;
                    this.customToastService.successToast('DB lookup changes saved successfully', 'Success');
                    this.ngxUiLoaderService.stop();

                    if (this.proceedAfterSave) {
                        this.pendingFocusRowKey = this.pendingNavigationRowKey;
                        this.pendingNavigationRowKey = null;
                        this.proceedAfterSave = false;
                    }

                    this.loadData();
                },
                error: () => {
                    this.proceedAfterSave = false;
                    this.ngxUiLoaderService.stop();
                    this.customToastService.errorToast('Failed to save DB lookup changes', 'Error');
                    this.cd.markForCheck();
                },
            });
    }

    // + button visibility: shown on group rows (to add items) and on region/country items (to add nested children).
    allowAdding = (e: any) => {
        const rowData: LookupTreeNode = e?.row?.data;
        if (rowData?.rowType === 'group' && rowData?.supportsCrud) {
            return true;
        }
        if (rowData?.rowType === 'item' && rowData?.section === 'regions' && rowData?.supportsCrud) {
            return true;
        }
        if (rowData?.rowType === 'item' && rowData?.section === 'countries' && rowData?.supportsCrud) {
            return true;
        }
        return false;
    };

    // Delete icon visible only on item rows (not section headers) that support CRUD.
    allowDeleting = (e: any) => {
        const rowData: LookupTreeNode = e?.row?.data;
        return rowData?.rowType === 'item' && rowData?.supportsCrud;
    };

    allowAddingGrade = (e: any) => {
        return this.hasChildren(e?.row?.data, this.gradeMatrixData);
    };

    allowDeletingGrade = (e: any) => {
        return this.hasChildren(e?.row?.data, this.gradeMatrixData);
    };

    onCustomAddClick = (e: any) => {
        if (this.treeList && this.treeList.instance) {
            const parentId = e?.row?.key;
            if (parentId !== undefined) {
                 this.treeList.instance.addRow(parentId);
            } else {
                 this.treeList.instance.addRow();
            }
        }
    };

    onCustomGradeAddClick = (e: any) => {
        if (this.treeListGradeMatrix && this.treeListGradeMatrix.instance) {
            const parentId = e?.row?.key;
            if (parentId !== undefined) {
                 this.treeListGradeMatrix.instance.addRow(parentId);
            } else {
                 this.treeListGradeMatrix.instance.addRow();
            }
        }
    };

    saveAllChanges(): void {
        if (this.treeList) {
            this.treeList.instance.saveEditData();
        }
    }

    undoAllChanges(): void {
        if (this.treeList) {
            this.treeList.instance.cancelEditData();
        }
        this.disableEdit = true;
        this.disableCancel = true;
        this.cd.markForCheck();
    }

    saveGradeMatrixChanges(): void {
        this.customToastService.successToast('Grade matrix changes saved successfully', 'Success');
        this.disableEditGrade = true;
        this.disableCancelGrade = true;
        this.cd.markForCheck();
    }

    undoGradeMatrixChanges(): void {
        if (this.treeListGradeMatrix) {
            this.treeListGradeMatrix.instance.cancelEditData();
        }
        this.disableEditGrade = true;
        this.disableCancelGrade = true;
    }

    // Builds the static in-memory grade matrix hierarchy (not persisted to the API).
    private initializeGradeMatrixData(): void {
        this.gradeMatrixData = [
            { id: 1000, parentId: 0, name: 'Grade Matrix' },
            { id: 1010, parentId: 1000, name: 'Level 1' },
            { id: 1011, parentId: 1010, name: 'Trainee' },
            { id: 1012, parentId: 1010, name: 'Assistant' },
            { id: 1020, parentId: 1000, name: 'Level 2' },
            { id: 1021, parentId: 1020, name: 'Technician' },
            { id: 1022, parentId: 1020, name: 'Senior Technician' },
            { id: 1030, parentId: 1000, name: 'Level 3' },
            { id: 1031, parentId: 1030, name: 'Specialist' },
            { id: 1032, parentId: 1030, name: 'Lead Specialist' },
            { id: 1040, parentId: 1000, name: 'Level 4' },
            { id: 1041, parentId: 1040, name: 'Supervisor' },
            { id: 1042, parentId: 1040, name: 'Assessor' },
            { id: 1043, parentId: 1040, name: 'Verifier' },
        ];
    }

    // Returns true if any node in treeData has rowData as its parent (used to decide + / delete visibility in grade matrix).
    private hasChildren(rowData: any, treeData: any[]): boolean {
        if (!rowData?.id) {
            return false;
        }

        return treeData.some((item) => item.parentId === rowData.id);
    }

    // Removes duplicate entries from a country list, keeping the first occurrence of each countryId.
    private deduplicateCountries(countries: CountryOption[]): CountryOption[] {
        const seen = new Set<string>();

        return countries.filter((country) => {
            if (!country?.countryId || seen.has(country.countryId)) {
                return false;
            }

            seen.add(country.countryId);
            return true;
        });
    }

    // Extracts a flat CountryOption list from the nested region→country hierarchy response.
    private flattenCountriesFromHierarchy(regionHierarchy: any[]): CountryOption[] {
        if (!Array.isArray(regionHierarchy) || !regionHierarchy.length) {
            return [];
        }

        return regionHierarchy.reduce((countries: CountryOption[], region: any) => {
            const regionCountries = Array.isArray(region?.countries) ? region.countries : [];

            regionCountries.forEach((country: any) => {
                countries.push({
                    countryId: country?.countryId,
                    name: country?.name,
                    code: country?.code,
                    editedDate: country?.editedDate,
                    region: {
                        regionId: region?.regionId,
                        name: region?.name,
                        editedDate: region?.editedDate,
                    },
                });
            });

            return countries;
        }, []);
    }

    // Converts the raw API responses into the flat LookupTreeNode array consumed by the tree-list.
    // Regions are expanded into their nested countries and bases; all other sections are flat items.
    private buildLookupTreeData(response: any): LookupTreeNode[] {
        const nodes: LookupTreeNode[] = [];

        LOOKUP_SECTION_CONFIG.forEach((config) => {
            nodes.push(this.buildRootNode(config));

            if (config.key === 'regions') {
                const regionHierarchy: any[] = response?.regionHierarchy?.response || [];
                regionHierarchy.forEach((region: any, rIndex: number) => {
                    const regionNode = this.buildItemNode(config, region, rIndex);
                    nodes.push(regionNode);

                    const regionCountries = Array.isArray(region?.countries) ? region.countries : [];
                    regionCountries.forEach((country: any, cIndex: number) => {
                        const countryNode: LookupTreeNode = {
                            id: `countries:${country.countryId || cIndex}`,
                            parentId: regionNode.id,
                            name: country.name || 'Unknown',
                            section: 'countries',
                            rowType: 'item',
                            supportsCrud: true,
                            entityId: country.countryId,
                            regionId: region.regionId,
                            code: country.code ?? null,
                            index: null,
                            comments: null,
                            countryId: null,
                            locationType: null,
                        };
                        nodes.push(countryNode);

                        const countryBases = Array.isArray(country?.bases) ? country.bases : [];
                        countryBases.forEach((base: any, bIndex: number) => {
                            nodes.push({
                                id: `bases:${base.locationId || bIndex}`,
                                parentId: countryNode.id,
                                name: base.name || base.locationName || 'Unknown',
                                section: 'bases',
                                rowType: 'item',
                                supportsCrud: true,
                                entityId: base.locationId,
                                countryId: country.countryId,
                                locationType: 1,
                                index: null,
                                comments: null,
                                regionId: region.regionId,
                                code: null,
                            });
                        });
                    });
                });
            } else {
                const items = response?.[config.key]?.response || [];
                items.forEach((item: any, index: number) => nodes.push(this.buildItemNode(config, item, index)));
            }
        });

        return nodes;
    }

    // Creates the non-editable group header node for a section (e.g. "Contract Status").
    private buildRootNode(config: LookupSectionConfig): LookupTreeNode {
        return {
            id: config.rootId,
            parentId: 0,
            name: config.label,
            section: config.key,
            rowType: 'group',
            supportsCrud: config.supportsCrud,
            locationType: config.locationType ?? null,
        };
    }

    // Creates an editable item node for a single API entity, mapping section-specific fields.
    private buildItemNode(config: LookupSectionConfig, item: any, index: number): LookupTreeNode {
        const entityId = this.resolveEntityId(config.key, item, index);

        return {
            id: this.buildNodeId(config.key, entityId, index),
            parentId: config.rootId,
            name: item?.name || item?.categoryName || item?.statusName || item?.jobTitle || item?.locationName || 'Unknown',
            section: config.key,
            rowType: 'item',
            supportsCrud: config.supportsCrud,
            entityId,
            index: config.key === 'assessmentCategories' ? (item?.index ?? null) : null,
            comments: config.key === 'contractStatuses' ? (item?.comments ?? '') : null,
            countryId: this.isLocationSection(config.key) ? (item?.countryId ?? item?.country?.countryId ?? null) : null,
            locationType: config.locationType ?? item?.locationType ?? null,
        };
    }

    // Extracts the correct entity ID field for each section from the raw API item, falling back to an index-based placeholder.
    private resolveEntityId(section: LookupSection, item: any, index: number): string {
        switch (section) {
            case 'assessmentCategories':
                return item?.assessmentCategoryId || `assessment-category-${index}`;
            case 'contractStatuses':
                return item?.contractStatusId || `contract-status-${index}`;
            case 'grades':
                return item?.gradeId || `grade-${index}`;
            case 'jobTitles':
                return item?.jobTitleId || `job-title-${index}`;
            case 'skills':
                return item?.skillId || `skill-${index}`;
            case 'regions':
                return item?.regionId || `region-${index}`;
            case 'countries':
                return item?.countryId || `country-${index}`;
            case 'bases':
            case 'trainingVenues':
                return item?.locationId || `location-${index}`;
            default:
                return `item-${index}`;
        }
    }

    // Generates a stable, unique tree-list row key in the format "section:entityId".
    private buildNodeId(section: LookupSection, entityId: string, index: number): string {
        return `${section}:${entityId || index}`;
    }

    private findNodeById(id: string): LookupTreeNode | undefined {
        return this.data.find((item) => item.id === id);
    }

    // Returns the next unused assessment category index (max existing + 1).
    private getNextAssessmentCategoryIndex(): number {
        const usedIndexes = this.getReservedAssessmentCategoryIndexes(this.getDbLookupChanges());
        const currentMax = Array.from(usedIndexes).reduce((maxValue, index) => Math.max(maxValue, index), 0);
        return currentMax + 1;
    }

    // Collects all index values already in use across saved data and pending changes to prevent index collisions.
    private getReservedAssessmentCategoryIndexes(changes: any[], includeInsertChanges = true): Set<number> {
        const reservedIndexes = new Set<number>();

        this.data
            .filter((item) => item.section === 'assessmentCategories' && item.rowType === 'item')
            .forEach((item) => {
                const parsedIndex = Number(item.index);
                if (Number.isInteger(parsedIndex) && parsedIndex > 0) {
                    reservedIndexes.add(parsedIndex);
                }
            });

        (changes || []).forEach((change: any) => {
            if (change?.type !== 'insert' && change?.type !== 'update') {
                return;
            }

            if (change?.type === 'insert' && !includeInsertChanges) {
                return;
            }

            const section = this.resolveChangeSection(change);
            if (section !== 'assessmentCategories') {
                return;
            }

            const existingNode = change?.type === 'update' ? this.findNodeById(change?.key) : null;
            const updatedIndex = Number(change?.data?.index ?? existingNode?.index);
            if (Number.isInteger(updatedIndex) && updatedIndex > 0) {
                reservedIndexes.add(updatedIndex);
            }
        });

        return reservedIndexes;
    }

    // Returns true for sections whose items have a location type (bases and training venues).
    private isLocationSection(section?: LookupSection): boolean {
        return section === 'bases' || section === 'trainingVenues';
    }

    // Checks the pending batch changes to see if a row is queued for deletion (for visual marking).
    private isRowMarkedForDelete(rowId: string): boolean {
        const changes = this.getDbLookupChanges();
        return changes.some((change: any) => change?.type === 'remove' && change?.key === rowId);
    }

    // Normalises a value to a trimmed, lowercase string for case-insensitive comparisons.
    private normalizeLookupValue(value: any): string {
        if (value == null) {
            return '';
        }

        return String(value).trim().toLowerCase();
    }

    // Shared character whitelist used by DevExtreme rules and TS-side save-state validation.
    private containsOnlyAllowedCharacters(value: any): boolean {
        const normalizedValue = String(value ?? '');
        return /^[A-Za-z0-9 .,!?'"_()\-]+$/.test(normalizedValue);
    }

    // Grade values must be whole numbers in the range 1–5.
    private isValidGradeValue(value: any): boolean {
        const parsedValue = Number(String(value ?? '').trim());
        return Number.isInteger(parsedValue) && parsedValue >= 1 && parsedValue <= 5;
    }

    // Scans the current batch changes to determine which section is being edited
    // (used to detect when the user navigates away before saving).
    private resolveActiveEditingSection(): LookupSection | null {
        const changes = this.getDbLookupChanges();
        for (const change of changes) {
            const section = this.resolveChangeSection(change);
            if (section) {
                return section;
            }
        }

        return null;
    }

    // Derives the LookupSection for a single pending change (insert resolves from parent; update/delete from existing node).
    private resolveChangeSection(change: any): LookupSection | null {
        if (change?.type === 'insert') {
            const scope = this.resolveInsertLayerScope(change);
            return scope?.section ?? null;
        }

        const existing = this.findNodeById(change?.key);
        return existing?.section ?? null;
    }

    // Returns true when the user clicks a row in a different section while unsaved changes exist,
    // meaning navigation should be blocked until the user saves or discards.
    private shouldInterceptCategorySwitch(rowData: LookupTreeNode | undefined): boolean {
        if (!rowData || rowData.rowType !== 'item') {
            return false;
        }

        const hasChanges = this.treeList?.instance?.hasEditData?.() ?? false;
        if (!hasChanges || !this.activeEditingSection) {
            return false;
        }

        return rowData.section !== this.activeEditingSection;
    }

    // If a cross-section navigation should be blocked, records the target row, suppresses the event
    // and opens the unsaved-changes modal. Returns true if navigation was intercepted.
    private tryInterceptNavigation(rowData: LookupTreeNode | undefined, key: any, event: any): boolean {
        if (!this.shouldInterceptCategorySwitch(rowData)) {
            return false;
        }

        this.pendingNavigationRowKey = key != null ? String(key) : null;
        event?.preventDefault?.();
        event?.stopPropagation?.();
        this.openUnsavedChangesModal();
        return true;
    }

    private updateSecondColumnVisibilityBySection(section: LookupSection | null | undefined): void {
        // Second column is always visible; nothing to update.
    }

    public getCodePlaceholder(rowData: any): string {
        if (!rowData || rowData.rowType !== 'item' || !rowData.supportsCrud) {
            return '';
        }
        if (rowData.section === 'countries') {
            return 'Enter country code';
        }
        if (rowData.section === 'contractStatuses') {
            return 'Enter comment';
        }
        if (rowData.section === 'trainingVenues') {
            return 'Select country';
        }
        return '';
    }

    public getNamePlaceholder(section: LookupSection | null | undefined): string {
        switch (section) {
            case 'assessmentCategories':
                return 'Enter assessment category';
            case 'contractStatuses':
                return 'Enter contract status';
            case 'grades':
                return 'Enter grade';
            case 'jobTitles':
                return 'Enter job title';
            case 'skills':
                return 'Enter skill';
            case 'regions':
                return 'Enter region';
            case 'countries':
                return 'Enter country';
            case 'bases':
                return 'Enter base';
            case 'trainingVenues':
                return 'Enter training or assessment venue';
            default:
                return 'Enter value';
        }
    }

    // Opens the "Unsaved Changes" confirmation modal. Guards against re-opening if already open.
    private openUnsavedChangesModal(): void {
        if (!this.unsavedChangesModal || this.unsavedChangesModalRef) {
            return;
        }

        this.unsavedChangesModalRef = this.modalService.open(this.unsavedChangesModal, {
            backdrop: 'static',
            keyboard: false,
            centered: true,
        });

        this.unsavedChangesModalRef.result.finally(() => {
            this.unsavedChangesModalRef = null;
        });
    }

    // Promotes the pending navigation key to the focus key so focusPendingRowIfNeeded
    // scrolls to it on the next data load.
    private proceedToPendingRow(): void {
        this.pendingFocusRowKey = this.pendingNavigationRowKey;
        this.pendingNavigationRowKey = null;
        this.focusPendingRowIfNeeded();
    }

    // After a data reload, focuses the previously-targeted row using DevExtreme's focusedRowKey option.
    private focusPendingRowIfNeeded(): void {
        const rowKey = this.pendingFocusRowKey;
        if (!rowKey || !this.treeList?.instance) {
            return;
        }

        this.pendingFocusRowKey = null;

        setTimeout(() => {
            this.treeList?.instance.option('focusedRowEnabled', true);
            this.treeList?.instance.option('focusedRowKey', rowKey);
        });
    }

    // Reads the current pending batch-edit changes from the DevExtreme tree-list instance.
    private getDbLookupChanges(): any[] {
        const changes = this.treeList?.instance?.option('editing.changes');
        return Array.isArray(changes) ? changes : [];
    }

    // Enables Save when there are changes with no validation errors; enables Reset when there are any changes.
    private updateDbLookupActionButtons(): void {
        const hasChanges = this.treeList?.instance?.hasEditData?.() ?? false;
        const hasValidationError = hasChanges && this.hasAnyLookupValidationError();

        this.disableEdit = !hasChanges || hasValidationError;
        this.disableCancel = !hasChanges;
        this.cd.markForCheck();
    }

    // TypeScript-side validation that runs on every change: checks for blank names, duplicate values,
    // invalid grade range and missing country on training venues. Controls Save button state.
    private hasAnyLookupValidationError(): boolean {
        const changes = this.getDbLookupChanges();
        if (!changes.length) {
            return false;
        }

        for (const change of changes) {
            if (change?.type !== 'insert' && change?.type !== 'update') {
                continue;
            }

            const candidate = this.resolveValidationCandidate(change);
            if (!candidate) {
                continue;
            }

            const normalizedName = this.normalizeLookupValue(candidate.name);
            if (!normalizedName) {
                return true;
            }

            if (!this.containsOnlyAllowedCharacters(candidate.name)) {
                return true;
            }

            if (candidate.section === 'grades' && !this.isValidGradeValue(candidate.name)) {
                return true;
            }

            const secondaryTextValue = this.resolveSecondaryTextValidationValue(change, candidate.section);
            if (secondaryTextValue && !this.containsOnlyAllowedCharacters(secondaryTextValue)) {
                return true;
            }

            if (candidate.section === 'trainingVenues' && !this.hasTrainingVenueCountrySelection(change)) {
                return true;
            }

            if (this.hasDuplicateInLayer(candidate.section, candidate.parentId, { id: candidate.id }, normalizedName)) {
                return true;
            }
        }

        return false;
    }

    // Extracts a minimal validation-friendly object (id, section, parentId, name) from a pending change.
    private resolveValidationCandidate(change: any): { id: string; section: LookupSection; parentId: string | number; name: string } | null {
        if (change?.type === 'insert') {
            const insertScope = this.resolveInsertLayerScope(change);
            if (!insertScope) {
                return null;
            }

            return {
                id: String(change?.key ?? ''),
                section: insertScope.section,
                parentId: insertScope.parentId,
                name: change?.data?.name || '',
            };
        }

        if (change?.type === 'update') {
            const existing = this.findNodeById(change?.key);
            if (!existing || existing.rowType !== 'item' || !existing.supportsCrud) {
                return null;
            }

            return {
                id: existing.id,
                section: change?.data?.section ?? existing.section,
                parentId: change?.data?.parentId ?? existing.parentId,
                name: change?.data?.name ?? existing.name,
            };
        }

        return null;
    }

    // Gets the editable text value from the shared second column where character validation applies.
    private resolveSecondaryTextValidationValue(change: any, section: LookupSection): string {
        if (section !== 'countries' && section !== 'contractStatuses') {
            return '';
        }

        if (change?.type === 'insert') {
            const insertedValue = section === 'countries' ? change?.data?.code : change?.data?.comments;
            return String(insertedValue ?? '').trim();
        }

        if (change?.type === 'update') {
            const existing = this.findNodeById(change?.key);
            const updatedValue = section === 'countries' ? (change?.data?.code ?? existing?.code) : (change?.data?.comments ?? existing?.comments);
            return String(updatedValue ?? '').trim();
        }

        return '';
    }

    // Checks whether a training venue change has a valid country selection.
    // For explicit country updates, validates the new value; for name-only updates, falls back to the existing countryId.
    private hasTrainingVenueCountrySelection(change: any): boolean {
        if (change?.type === 'insert') {
            const selectedCountryId = this.resolveCountryIdFromSelection(change?.data?.countryId) || this.resolveCountryIdFromSelection(change?.data?.code);
            return Boolean(selectedCountryId);
        }

        if (change?.type === 'update') {
            const existing = this.findNodeById(change?.key);
            const hasExplicitCountryUpdate = Object.prototype.hasOwnProperty.call(change?.data ?? {}, 'countryId');
            const hasExplicitCodeUpdate = Object.prototype.hasOwnProperty.call(change?.data ?? {}, 'code');

            if (hasExplicitCountryUpdate || hasExplicitCodeUpdate) {
                const selectedFromChange =
                    this.resolveCountryIdFromSelection(change?.data?.countryId) || this.resolveCountryIdFromSelection(change?.data?.code);
                return Boolean(selectedFromChange);
            }

            const selectedCountryId =
                this.resolveCountryIdFromSelection(change?.data?.countryId) ||
                this.resolveCountryIdFromSelection(change?.data?.code) ||
                this.resolveCountryIdFromSelection(existing?.countryId) ||
                this.resolveCountryIdFromSelection(existing?.code);
            return Boolean(selectedCountryId);
        }

        return true;
    }

    // Resolves a valid countryId from a raw value by matching it against countryOptions
    // in order of: direct countryId match → country code match → country name match.
    private resolveCountryIdFromSelection(value: any): string | null {
        const normalized = String(value ?? '').trim();
        if (!normalized) {
            return null;
        }

        const directMatch = this.countryOptions.find((country) => country.countryId === normalized);
        if (directMatch?.countryId) {
            return directMatch.countryId;
        }

        const byCode = this.countryOptions.find(
            (country) =>
                String(country?.code ?? '')
                    .trim()
                    .toLowerCase() === normalized.toLowerCase()
        );
        if (byCode?.countryId) {
            return byCode.countryId;
        }

        const byName = this.countryOptions.find(
            (country) =>
                String(country?.name ?? '')
                    .trim()
                    .toLowerCase() === normalized.toLowerCase()
        );
        if (byName?.countryId) {
            return byName.countryId;
        }

        return null;
    }

    // Determines the section and parentId that define the uniqueness layer for a row
    // (used by the duplicate-name validation rule).
    private resolveLayerScope(rowData: Partial<LookupTreeNode> | undefined): { section: LookupSection; parentId: string | number } | null {
        if (!rowData) {
            return null;
        }

        if (rowData.section && rowData.parentId != null) {
            return { section: rowData.section, parentId: rowData.parentId };
        }

        if (rowData.id) {
            const existing = this.findNodeById(rowData.id);
            if (existing?.section && existing.parentId != null) {
                return { section: existing.section, parentId: existing.parentId };
            }
        }

        return null;
    }

    // Returns true if another row in the same section/parent layer already has the same normalised name,
    // accounting for pending inserts, updates and deletes so in-flight duplicates are also caught.
    private hasDuplicateInLayer(
        section: LookupSection,
        parentId: string | number,
        currentRowData: Partial<LookupTreeNode> | undefined,
        normalizedInput: string
    ): boolean {
        const removedIds = new Set<string>();
        const updatedNames = new Map<string, string>();
        const insertedInLayer: Array<{ key: string; name: string }> = [];

        this.getDbLookupChanges().forEach((change: any) => {
            if (change?.type === 'remove' && change?.key) {
                removedIds.add(change.key);
                return;
            }

            if (change?.type === 'update' && change?.key && change?.data?.name != null) {
                updatedNames.set(change.key, change.data.name);
                return;
            }

            if (change?.type !== 'insert') {
                return;
            }

            const insertScope = this.resolveInsertLayerScope(change);
            if (!insertScope) {
                return;
            }

            if (insertScope.section === section && insertScope.parentId === parentId) {
                insertedInLayer.push({
                    key: String(change?.key ?? ''),
                    name: change?.data?.name || '',
                });
            }
        });

        const existingHasDuplicate = this.data
            .filter((row) => row.rowType === 'item' && row.section === section && row.parentId === parentId)
            .filter((row) => !removedIds.has(row.id))
            .filter((row) => !currentRowData?.id || row.id !== currentRowData.id)
            .map((row) => updatedNames.get(row.id) ?? row.name)
            .some((name) => this.normalizeLookupValue(name) === normalizedInput);

        if (existingHasDuplicate) {
            return true;
        }

        let insertMatchCount = insertedInLayer.filter((item) => this.normalizeLookupValue(item.name) === normalizedInput).length;

        if (insertMatchCount === 0) {
            return false;
        }

        const currentRowId = String(currentRowData?.id ?? '');
        if (currentRowId) {
            const hasCurrentInsertEntry = insertedInLayer.some((item) => item.key === currentRowId && this.normalizeLookupValue(item.name) === normalizedInput);
            if (hasCurrentInsertEntry) {
                insertMatchCount -= 1;
            }
        } else {
            // For brand new rows without a stable id in validation context, one insert match is the current edit row.
            insertMatchCount -= 1;
        }

        return insertMatchCount > 0;
    }

    // For an insert change, resolves which section and parentId the new row belongs to by
    // inspecting change.data.section or the parent node's section and rowType.
    private resolveInsertLayerScope(change: any): { section: LookupSection; parentId: string | number } | null {
        const parentId = change?.data?.parentId ?? change?.parentKeys?.[0] ?? change?.parentKey;
        if (parentId == null) {
            return null;
        }

        const explicitSection = change?.data?.section;
        if (explicitSection) {
            return { section: explicitSection, parentId };
        }

        const parentNode = this.findNodeById(parentId);
        if (!parentNode) {
            return null;
        }

        if (parentNode.rowType === 'group') {
            return { section: parentNode.section, parentId };
        }

        if (parentNode.section === 'regions' && parentNode.rowType === 'item') {
            return { section: 'countries', parentId };
        }

        if (parentNode.section === 'countries' && parentNode.rowType === 'item') {
            return { section: 'bases', parentId };
        }

        return null;
    }

    // Converts the DevExtreme batch-edit changes into grouped API request Observables.
    // Builds create/update/delete payload buckets per section, validates each payload,
    // then assembles the final Observable array for forkJoin.
    private buildPersistenceRequests(changes: any[]): { requests: Observable<any>[]; error?: string } {
        const createPayloads = this.createSectionBuckets();
        const updatePayloads = this.createSectionBuckets();
        const deletePayloads = this.createSectionBuckets();
        const usedAssessmentIndexes = this.getReservedAssessmentCategoryIndexes(changes, false);
        let nextAssessmentIndex = Array.from(usedAssessmentIndexes).reduce((maxValue, index) => Math.max(maxValue, index), 0) + 1;

        for (const change of changes) {
            const node = this.resolveChangeNode(change, nextAssessmentIndex);
            if (!node) {
                continue;
            }

            if (change.type === 'insert' && node.section === 'assessmentCategories') {
                while (usedAssessmentIndexes.has(nextAssessmentIndex)) {
                    nextAssessmentIndex += 1;
                }

                node.index = nextAssessmentIndex;
                usedAssessmentIndexes.add(nextAssessmentIndex);
                nextAssessmentIndex += 1;
            }

            if (change.type === 'insert') {
                const createPayload = this.buildCreatePayload(node);
                if (typeof createPayload === 'string') {
                    return { requests: [], error: createPayload };
                }
                if (createPayload) {
                    createPayloads[node.section].push(createPayload);
                }
                continue;
            }

            if (change.type === 'update') {
                const updatePayload = this.buildUpdatePayload(node);
                if (typeof updatePayload === 'string') {
                    return { requests: [], error: updatePayload };
                }
                if (updatePayload) {
                    updatePayloads[node.section].push(updatePayload);
                }
                continue;
            }

            if (change.type === 'remove') {
                const deletePayload = this.buildDeletePayload(node);
                if (typeof deletePayload === 'string') {
                    return { requests: [], error: deletePayload };
                }
                if (deletePayload) {
                    deletePayloads[node.section].push(deletePayload);
                }
            }
        }

        const requests: Observable<any>[] = [];
        const allSections: LookupSection[] = [
            'assessmentCategories',
            'contractStatuses',
            'grades',
            'jobTitles',
            'skills',
            'regions',
            'countries',
            'bases',
            'trainingVenues',
        ];

        allSections.forEach((key) => {
            if (createPayloads[key].length) {
                requests.push(this.createRequest(key, createPayloads[key]));
            }

            if (updatePayloads[key].length) {
                requests.push(this.updateRequest(key, updatePayloads[key]));
            }

            if (deletePayloads[key].length) {
                requests.push(this.deleteRequest(key, deletePayloads[key]));
            }
        });

        return { requests };
    }

    // Creates an empty payload bucket for every section, used to collect payloads before building API requests.
    private createSectionBuckets(): Record<LookupSection, any[]> {
        return {
            assessmentCategories: [],
            contractStatuses: [],
            grades: [],
            jobTitles: [],
            skills: [],
            regions: [],
            countries: [],
            bases: [],
            trainingVenues: [],
        };
    }

    // Merges a pending change with the corresponding existing node to produce the full data
    // required to build an API payload. Returns null for invalid/unsupported changes.
    private resolveChangeNode(change: any, nextAssessmentIndex: number): LookupTreeNode | null {
        if (change.type === 'insert') {
            const parentId = change?.data?.parentId ?? change?.parentKeys?.[0] ?? change?.parentKey;
            const parentNode = this.findNodeById(parentId);

            if (!parentNode || !parentNode.supportsCrud) {
                return null;
            }

            let childSection: LookupSection;
            let childLocationType: number | null = null;
            let childRegionId: string | null = null;
            let childCountryId: string | null = null;

            if (parentNode.rowType === 'group') {
                childSection = parentNode.section;
                childLocationType = parentNode.locationType ?? null;
            } else if (parentNode.section === 'regions' && parentNode.rowType === 'item') {
                childSection = 'countries';
                childRegionId = parentNode.entityId ?? null;
            } else if (parentNode.section === 'countries' && parentNode.rowType === 'item') {
                childSection = 'bases';
                childCountryId = parentNode.entityId ?? null;
                childRegionId = parentNode.regionId ?? null;
                childLocationType = 1;
            } else {
                return null;
            }

            return {
                id: '',
                parentId: parentNode.id,
                name: change?.data?.name || '',
                section: childSection,
                rowType: 'item',
                supportsCrud: true,
                index: change?.data?.index ?? (childSection === 'assessmentCategories' ? nextAssessmentIndex : null),
                comments: change?.data?.comments ?? '',
                countryId: change?.data?.countryId ?? childCountryId,
                regionId: change?.data?.regionId ?? childRegionId,
                code: change?.data?.code ?? null,
                locationType: change?.data?.locationType ?? childLocationType,
            };
        }

        const existingNode = this.findNodeById(change?.key);
        if (!existingNode || existingNode.rowType !== 'item' || !existingNode.supportsCrud) {
            return null;
        }

        if (change.type === 'update') {
            return {
                ...existingNode,
                ...change.data,
                name: change?.data?.name ?? existingNode.name,
                index: change?.data?.index ?? existingNode.index ?? null,
                comments: change?.data?.comments ?? existingNode.comments ?? '',
                countryId: change?.data?.countryId ?? existingNode.countryId ?? null,
                regionId: change?.data?.regionId ?? existingNode.regionId ?? null,
                code: change?.data?.code ?? existingNode.code ?? null,
                locationType: existingNode.locationType ?? null,
            };
        }

        return existingNode;
    }

    // Builds the API create payload for a node. Returns an error string if required fields are missing.
    private buildCreatePayload(node: LookupTreeNode): any {
        const name = node.name?.trim();
        if (!name) {
            return `${this.getSectionLabel(node.section)} name is required.`;
        }

        switch (node.section) {
            case 'assessmentCategories':
                return {
                    name,
                    index: Number(node.index ?? this.getNextAssessmentCategoryIndex()),
                };
            case 'contractStatuses':
                return {
                    name,
                    comments: node.comments?.trim() || '',
                };
            case 'grades':
                if (!this.isValidGradeValue(name)) {
                    return `Grade value must be between 1 and 5.`;
                }
                return { name };
            case 'jobTitles':
                return { name };
            case 'skills':
                return { name };
            case 'regions':
                return { name };
            case 'countries':
                if (!node.regionId) {
                    return `Region is required for Country.`;
                }

                if (!node.code?.trim()) {
                    return `Country code is required.`;
                }

                if (node.code.trim().length > 5) {
                    return `Country code must be less than or equal to 5 characters.`;
                }

                return { name, regionId: node.regionId, code: node.code.trim() };
            case 'bases':
                if (!node.countryId) {
                    return `Country is required for ${this.getSectionLabel(node.section)}.`;
                }

                return {
                    name,
                    countryId: node.countryId,
                    locationType: node.locationType ?? 1,
                };
            case 'trainingVenues':
                if (!node.countryId) {
                    return `Country is required for ${this.getSectionLabel(node.section)}.`;
                }

                return {
                    name,
                    countryId: node.countryId,
                    locationType: node.locationType,
                };
            default:
                return null;
        }
    }

    // Builds the API update payload for a node, including the entity ID. Returns an error string on failure.
    private buildUpdatePayload(node: LookupTreeNode): any {
        const name = node.name?.trim();
        if (!name || !node.entityId) {
            return `${this.getSectionLabel(node.section)} update is missing required data.`;
        }

        switch (node.section) {
            case 'assessmentCategories':
                return {
                    assessmentCategoryId: node.entityId,
                    name,
                    index: Number(node.index ?? this.getNextAssessmentCategoryIndex()),
                };
            case 'contractStatuses':
                return {
                    contractStatusId: node.entityId,
                    name,
                    comments: node.comments?.trim() || '',
                };
            case 'grades':
                if (!this.isValidGradeValue(name)) {
                    return `Grade value must be between 1 and 5.`;
                }
                return {
                    gradeId: node.entityId,
                    name,
                };
            case 'jobTitles':
                return {
                    jobTitleId: node.entityId,
                    name,
                };
            case 'skills':
                return {
                    skillId: node.entityId,
                    name,
                };
            case 'regions':
                return {
                    regionId: node.entityId,
                    name,
                };
            case 'countries':
                if (!node.regionId) {
                    return `Region is required for Country update.`;
                }

                if (!node.code?.trim()) {
                    return `Country code is required for Country update.`;
                }

                if (node.code.trim().length > 5) {
                    return `Country code must be less than or equal to 5 characters.`;
                }

                return {
                    countryId: node.entityId,
                    name,
                    regionId: node.regionId,
                    code: node.code.trim(),
                };
            case 'bases':
                if (!node.countryId) {
                    return `Country is required for ${this.getSectionLabel(node.section)}.`;
                }

                return {
                    locationId: node.entityId,
                    name,
                    countryId: node.countryId,
                    locationType: node.locationType ?? 1,
                };
            case 'trainingVenues':
                if (!node.countryId) {
                    return `Country is required for ${this.getSectionLabel(node.section)}.`;
                }

                return {
                    locationId: node.entityId,
                    name,
                    countryId: node.countryId,
                    locationType: node.locationType,
                };
            default:
                return null;
        }
    }

    // Builds the API soft-delete payload (active: false) for a node.
    private buildDeletePayload(node: LookupTreeNode): any {
        if (!node.entityId) {
            return `${this.getSectionLabel(node.section)} delete is missing the item id.`;
        }

        switch (node.section) {
            case 'assessmentCategories':
                return {
                    assessmentCategoryId: node.entityId,
                    active: false,
                };
            case 'contractStatuses':
                return {
                    contractStatusId: node.entityId,
                    active: false,
                };
            case 'grades':
                return {
                    gradeId: node.entityId,
                    active: false,
                };
            case 'jobTitles':
                return {
                    jobTitleId: node.entityId,
                    active: false,
                };
            case 'skills':
                return {
                    skillId: node.entityId,
                    active: false,
                };
            case 'regions':
                return {
                    regionId: node.entityId,
                    active: false,
                };
            case 'countries':
                return {
                    countryId: node.entityId,
                    active: false,
                };
            case 'bases':
                return {
                    locationId: node.entityId,
                    active: false,
                };
            case 'trainingVenues':
                return {
                    locationId: node.entityId,
                    active: false,
                };
            default:
                return null;
        }
    }

    // Routes the create payload to the correct API service method for the given section.
    private createRequest(section: LookupSection, payload: any[]): Observable<any> {
        switch (section) {
            case 'assessmentCategories':
                return this.competencyApiService.createAssessmentCategories(payload);
            case 'contractStatuses':
                return this.competencyApiService.createContractStatuses(payload);
            case 'grades':
                return this.competencyApiService.createGrades(payload);
            case 'jobTitles':
                return this.competencyApiService.createJobTitles(payload);
            case 'skills':
                return this.competencyApiService.createSkills(payload);
            case 'regions':
                return this.competencyApiService.createRegions(payload);
            case 'countries':
                return this.competencyApiService.createCountries(payload);
            case 'bases':
                return this.competencyApiService.createLocations(payload);
            case 'trainingVenues':
                return this.competencyApiService.createLocations(payload);
            default:
                throw new Error(`Create is not configured for ${section}`);
        }
    }

    // Routes the update payload to the correct API service method for the given section.
    private updateRequest(section: LookupSection, payload: any[]): Observable<any> {
        switch (section) {
            case 'assessmentCategories':
                return this.competencyApiService.updateAssessmentCategories(payload);
            case 'contractStatuses':
                return this.competencyApiService.updateContractStatuses(payload);
            case 'grades':
                return this.competencyApiService.updateGrades(payload);
            case 'jobTitles':
                return this.competencyApiService.updateJobTitles(payload);
            case 'skills':
                return this.competencyApiService.updateSkills(payload);
            case 'regions':
                return this.competencyApiService.updateRegions(payload);
            case 'countries':
                return this.competencyApiService.updateCountries(payload);
            case 'bases':
                return this.competencyApiService.updateLocations(payload);
            case 'trainingVenues':
                return this.competencyApiService.updateLocations(payload);
            default:
                throw new Error(`Update is not configured for ${section}`);
        }
    }

    // Routes the delete payload to the correct API service method for the given section.
    private deleteRequest(section: LookupSection, payload: any[]): Observable<any> {
        switch (section) {
            case 'assessmentCategories':
                return this.competencyApiService.deleteAssessmentCategories(payload);
            case 'contractStatuses':
                return this.competencyApiService.deleteContractStatuses(payload);
            case 'grades':
                return this.competencyApiService.deleteGrades(payload);
            case 'jobTitles':
                return this.competencyApiService.deleteJobTitles(payload);
            case 'skills':
                return this.competencyApiService.deleteSkills(payload);
            case 'regions':
                return this.competencyApiService.deleteRegions(payload);
            case 'countries':
                return this.competencyApiService.deleteCountries(payload);
            case 'bases':
                return this.competencyApiService.deleteLocations(payload);
            case 'trainingVenues':
                return this.competencyApiService.deleteLocations(payload);
            default:
                throw new Error(`Delete is not configured for ${section}`);
        }
    }

    // Returns a human-readable label for a section, used in validation error messages.
    private getSectionLabel(section: LookupSection): string {
        const fromConfig = LOOKUP_SECTION_CONFIG.find((config) => config.key === section)?.label;
        if (fromConfig) {
            return fromConfig;
        }
        switch (section) {
            case 'countries':
                return 'Country';
            case 'bases':
                return 'Base';
            default:
                return 'Lookup';
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
